import { useEffect, useMemo, useRef, useState } from "react";
import { Grid3x3, CheckCircle2 } from "lucide-react";
import GamesPageShell from "../../components/games/GamesPageShell";
import GameResultCard from "../../components/games/GameResultCard";
import { WORD_SEARCH_GRID, WORD_SEARCH_WORDS } from "../../content/games";
import { trackGameEvent } from "../../lib/gameEvents";

const ROWS = WORD_SEARCH_GRID.length;
const COLS = WORD_SEARCH_GRID[0].length;

function cellKey(r, c) { return `${r},${c}`; }
function pathsEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every(([r, c], i) => r === b[i][0] && c === b[i][1]);
}

// Straight-line path (row/col/diagonal, either direction) between two
// cells, inclusive. Returns null if the two cells don't share a row,
// column, or 45-degree diagonal.
function straightLinePath([r1, c1], [r2, c2]) {
  const dr = Math.sign(r2 - r1);
  const dc = Math.sign(c2 - c1);
  const steps = Math.max(Math.abs(r2 - r1), Math.abs(c2 - c1));
  if (steps === 0) return [[r1, c1]];
  if (r1 + dr * steps !== r2 || c1 + dc * steps !== c2) return null; // not a clean line
  const path = [];
  for (let i = 0; i <= steps; i++) path.push([r1 + dr * i, c1 + dc * i]);
  return path;
}

export default function WordFinderGame() {
  const [foundWords, setFoundWords] = useState(() => new Set());
  const [foundCells, setFoundCells] = useState(() => new Set());
  const [pendingAnchor, setPendingAnchor] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [runId, setRunId] = useState(0);
  const [statusMessage, setStatusMessage] = useState(""); // aria-live announcement, mirrors visual feedback
  const didDragRef = useRef(false);
  const statusTimerRef = useRef(null);

  function announce(message, ttlMs) {
    window.clearTimeout(statusTimerRef.current);
    setStatusMessage(message);
    if (ttlMs) statusTimerRef.current = window.setTimeout(() => setStatusMessage(""), ttlMs);
  }

  useEffect(() => () => window.clearTimeout(statusTimerRef.current), []);

  useEffect(() => { trackGameEvent("game_opened", { game: "word-finder" }); }, [runId]);

  const finished = foundWords.size === WORD_SEARCH_WORDS.length;

  useEffect(() => {
    if (!startedAt || finished) return;
    const t = window.setInterval(() => setElapsedSec(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => window.clearInterval(t);
  }, [startedAt, finished]);

  useEffect(() => {
    if (finished && startedAt) {
      trackGameEvent("word_finder_completion", { game: "word-finder", seconds: elapsedSec, words: WORD_SEARCH_WORDS.length });
      trackGameEvent("game_completed", { game: "word-finder" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  function markStarted() {
    if (!startedAt) setStartedAt(Date.now());
  }

  function tryFinalize(a, b, { fromTap = false } = {}) {
    const path = straightLinePath(a, b);
    if (!path || path.length < 2) {
      if (fromTap) announce("Those two letters aren't in a straight line: try again.", 2500);
      return;
    }
    const match = WORD_SEARCH_WORDS.find(
      (w) => !foundWords.has(w.word) && (pathsEqual(path, w.path) || pathsEqual([...path].reverse(), w.path)),
    );
    if (match) {
      setFoundWords((prev) => new Set(prev).add(match.word));
      setFoundCells((prev) => {
        const next = new Set(prev);
        match.path.forEach(([r, c]) => next.add(cellKey(r, c)));
        return next;
      });
      trackGameEvent("word_finder_word_found", { game: "word-finder", word: match.word });
      if (fromTap) announce(`Found: ${match.word}`, 2500);
    } else if (fromTap) {
      announce("No word there: try again.", 2500);
    }
  }

  function handlePointerDown(r, c) {
    markStarted();
    didDragRef.current = false;
    setIsDragging(true);
    setDragStart([r, c]);
    setDragCurrent([r, c]);
  }

  function handlePointerEnter(r, c) {
    if (!isDragging) return;
    didDragRef.current = true;
    setDragCurrent([r, c]);
  }

  function handlePointerUp() {
    if (isDragging && didDragRef.current && dragStart && dragCurrent) {
      tryFinalize(dragStart, dragCurrent);
    }
    setIsDragging(false);
    setDragStart(null);
    setDragCurrent(null);
  }

  function handleClick(r, c) {
    markStarted();
    if (didDragRef.current) { didDragRef.current = false; return; }
    if (!pendingAnchor) {
      setPendingAnchor([r, c]);
      announce(`First letter selected: row ${r + 1}, column ${c + 1}. Now select the last letter of the word.`);
    } else {
      tryFinalize(pendingAnchor, [r, c], { fromTap: true });
      setPendingAnchor(null);
    }
  }

  const previewPath = useMemo(() => {
    if (isDragging && dragStart && dragCurrent) return straightLinePath(dragStart, dragCurrent) || [dragStart];
    return null;
  }, [isDragging, dragStart, dragCurrent]);
  const previewSet = useMemo(() => new Set((previewPath || []).map(([r, c]) => cellKey(r, c))), [previewPath]);

  function replay() {
    setFoundWords(new Set());
    setFoundCells(new Set());
    setPendingAnchor(null);
    setDragStart(null);
    setDragCurrent(null);
    setIsDragging(false);
    setStartedAt(null);
    setElapsedSec(0);
    window.clearTimeout(statusTimerRef.current);
    setStatusMessage("");
    setRunId((n) => n + 1);
  }

  return (
    <GamesPageShell
      title="Word Finder"
      description="Find the rental vocabulary hidden in the grid. Drag across letters, or tap the first letter then the last."
    >
      {finished ? (
        <GameResultCard
          icon={Grid3x3}
          tone="emerald"
          eyebrow="Puzzle complete"
          headline={`${WORD_SEARCH_WORDS.length} of ${WORD_SEARCH_WORDS.length} words found`}
          scoreText={`${elapsedSec}s`}
          subtext="Nice work: every word was hiding somewhere in the grid."
          onReplay={replay}
        />
      ) : (
        <div className="space-y-5" onPointerUp={handlePointerUp} onPointerLeave={() => isDragging && handlePointerUp()}>
          <div className="flex justify-between items-center text-xs text-slate-500">
            <span data-testid="wf-progress-text">{foundWords.size} of {WORD_SEARCH_WORDS.length} words found</span>
            <span data-testid="wf-timer">{elapsedSec}s</span>
          </div>

          {/* Visually hidden live region: announces the tap-two-endpoints flow
              (anchor selected / found / no match) for screen-reader users, since
              the grid's find/miss feedback is otherwise conveyed visually only. */}
          <p className="sr-only" role="status" aria-live="polite" data-testid="wf-status-message">{statusMessage}</p>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <p className="text-xs text-slate-500 mb-3">
              Hard to drag precisely, especially on a small screen? Tap the word's first letter, then its last
              letter: no dragging needed.
            </p>
            <div className="overflow-x-auto" data-testid="wf-grid">
              {/*
                Cells stay 26px on mobile (12-wide grid: 44px cells here would
                force horizontal scrolling / break the puzzle's fit). Instead
                each button's real hit target is expanded a few px past its
                visible box via an absolutely-positioned pseudo-element, so the
                tap target is more forgiving without growing the visible grid.
                Selection state is signalled with more than color (underline +
                border weight + a ring), for non-color-blind-only feedback.
              */}
              <table className="border-collapse select-none mx-auto" style={{ touchAction: "none" }}>
                <tbody>
                  {WORD_SEARCH_GRID.map((row, r) => (
                    <tr key={r}>
                      {row.map((letter, c) => {
                        const key = cellKey(r, c);
                        const isFound = foundCells.has(key);
                        const isPreview = previewSet.has(key);
                        const isAnchor = pendingAnchor && pendingAnchor[0] === r && pendingAnchor[1] === c;
                        let cls = "border-slate-200 bg-white text-slate-700 hover:bg-emerald-50";
                        if (isFound) cls = "border-2 border-emerald-700 bg-emerald-500 text-white underline decoration-2 underline-offset-2";
                        else if (isPreview) cls = "border-2 border-dashed border-emerald-500 bg-emerald-200 text-emerald-900";
                        else if (isAnchor) cls = "border-2 border-emerald-600 ring-2 ring-emerald-300 ring-offset-1 bg-emerald-100 text-emerald-900";
                        return (
                          <td key={c} className="p-0">
                            <button
                              type="button"
                              data-testid={`wf-cell-${r}-${c}`}
                              aria-pressed={isFound}
                              aria-label={`Row ${r + 1}, column ${c + 1}, letter ${letter}${isFound ? ", part of a found word" : isAnchor ? ", selected as first letter" : ""}`}
                              onPointerDown={() => handlePointerDown(r, c)}
                              onPointerEnter={() => handlePointerEnter(r, c)}
                              onClick={() => handleClick(r, c)}
                              className={`relative w-[26px] h-[26px] sm:w-9 sm:h-9 text-[11px] sm:text-sm font-semibold grid place-items-center border ${cls} transition-colors
                                before:content-[''] before:absolute before:-inset-[5px] sm:before:inset-0
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2`}
                            >
                              {letter}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <div className="text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">Words to find</div>
            <div className="flex flex-wrap gap-2" data-testid="wf-word-list">
              {WORD_SEARCH_WORDS.map((w) => {
                const found = foundWords.has(w.word);
                return (
                  <span
                    key={w.word}
                    data-testid={`wf-word-${w.word}`}
                    className={`min-h-11 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border ${
                      found ? "border-emerald-300 bg-emerald-50 text-emerald-800 line-through" : "border-slate-200 bg-slate-50 text-slate-700"
                    }`}
                  >
                    {found && <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />}
                    {w.word}
                  </span>
                );
              })}
            </div>
            {/* The grid's word paths are fixed coordinates, so DEPOSIT cannot be
                split into two entries without regenerating the puzzle. The
                distinction still matters, so it is stated here instead. */}
            <p className="mt-3 text-xs text-slate-500" data-testid="wf-deposit-note">
              In ConfirmPH, <strong>deposit</strong> means a refundable security deposit held by the
              rental business under the rental terms. It is not the same as an advance or
              downpayment, which goes toward the rental price.
            </p>
          </div>
        </div>
      )}
    </GamesPageShell>
  );
}
